<template>
  <a-modal
    :open="open"
    centered
    title="Объекты для комнат"
    :width="1240"
    :body-style="{ maxHeight: '78vh', overflowY: 'auto' }"
    :mask-closable="false"
    :keyboard="false"
    :footer="null"
    @cancel="cancel">
    <div class="room-field">
      <strong>Типы комнат</strong>
      <div class="room-actions">
        <a-button
          type="link"
          size="small"
          @click="rooms = availableRooms.map(room => room.id)">
          Выбрать все
        </a-button>
        <a-button
          type="link"
          size="small"
          @click="rooms = []">
          Убрать все
        </a-button>
      </div>
      <a-select
        v-model:value="rooms"
        mode="multiple"
        :max-tag-count="4"
        show-search
        option-filter-prop="label"
        :options="availableRooms.map(room => ({ value: room.id, label: room.name }))"
        placeholder="Выберите один или несколько типов комнат"
        style="width: 100%" />
    </div>
    <div class="purpose-field">
      <strong>Назначение объектов</strong>
      <a-radio-group
        v-model:value="purpose"
        button-style="solid">
        <a-radio-button value="required">
          Обязательные — для проверки
        </a-radio-button>
        <a-radio-button value="default">
          Дефолтные — применить в конструкторе
        </a-radio-button>
      </a-radio-group>
      <span class="purpose-hint">{{ purpose === 'default'
        ? 'Материалы, двери, окна и молдинги будут применены автоматически. Проверки для них не создаются.'
        : 'Для выбранных объектов можно создать проверки планировки.' }}</span>
    </div>
    <a-tabs
      v-model:activeKey="category"
      :destroy-inactive-tab-pane="false">
      <a-tab-pane
        v-for="tab in visibleTabs"
        :key="tab.key"
        :tab="tab.label"
        force-render>
        <div v-if="tab.key === 'furniture'">
          <object-asset-browser
            category="furniture"
            :selected-uuids="selectedFor('furniture', '')"
            @select="toggle($event, '')" />
        </div>
        <a-collapse
          v-else
          :destroy-inactive-panel="false">
          <a-collapse-panel
            v-for="variant in tab.variants"
            :key="variant.key"
            :header="`${variant.label} · ${selectedFor(tab.key, variant.key).length} выбрано`"
            force-render>
            <object-asset-browser
              :category="tab.key"
              :surface="tab.key === 'aperture' ? null : variant.key"
              :aperture-type="tab.key === 'aperture' ? variant.key : null"
              fixed-surface
              :selected-uuids="selectedFor(tab.key, variant.key)"
              @select="toggle($event, variant.key)" />
          </a-collapse-panel>
        </a-collapse>
      </a-tab-pane>
    </a-tabs>
    <div class="modal-footer">
      <span>Типов комнат: {{ rooms.length }} · Объектов: {{ objects.length }}</span>
      <a-button
        danger
        @click="cancel">
        Отменить
      </a-button>
      <a-button
        type="primary"
        :disabled="!rooms.length || !objects.length"
        @click="save">
        Сохранить набор
      </a-button>
    </div>
  </a-modal>
</template>

<script setup>
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { Modal } from 'ant-design-vue';
import '@/assets/styles/defaultsConfirm.css';
import ObjectAssetBrowser from './ObjectAssetBrowser.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  roomTypes: { type: Array, default: () => [] },
  occupiedGroups: { type: Array, default: () => [] },
  group: { type: Object, default: null },
});
const emit = defineEmits(['close', 'save']);
const rooms = ref([]);
const objects = ref([]);
const category = ref('furniture');
const purpose = ref('required');
const tabs = [
  { key: 'furniture', label: 'Мебель и 3D-объекты' },
  {
    key: 'material',
    label: 'Материалы',
    variants: [
      { key: 'wall', label: 'Стены' }, { key: 'floor', label: 'Пол' }, { key: 'ceiling', label: 'Потолок' },
    ],
  },
  {
    key: 'aperture',
    label: 'Двери и окна',
    variants: [
      { key: 'door', label: 'Двери' }, { key: 'window', label: 'Окна' },
    ],
  },
  {
    key: 'molding',
    label: 'Молдинги',
    variants: [
      { key: 'floor', label: 'Напольный' }, { key: 'ceiling', label: 'Потолочный' },
    ],
  },
];
const visibleTabs = computed(() => tabs.filter((tab) => purpose.value !== 'default' || tab.key !== 'furniture'));
const availableRooms = computed(() => props.roomTypes.filter((room) => !props.occupiedGroups.some(
  (group) => group.purpose === purpose.value
    && (group.rooms.includes(null) || group.rooms.includes(room.id)),
)));
watch(purpose, (value) => {
  rooms.value = rooms.value.filter((id) => availableRooms.value.some((room) => room.id === id));
  if (value === 'default') {
    objects.value = objects.value.filter((item) => item.asset_category !== 'furniture');
    if (category.value === 'furniture') category.value = 'material';
  }
});
watch(() => props.open, (open) => {
  if (!open) return;
  purpose.value = props.group?.purpose || 'required';
  rooms.value = props.group?.rooms.includes(null)
    ? availableRooms.value.map((room) => room.id) : [...(props.group?.rooms || [])];
  objects.value = (props.group?.objects || []).map((item) => ({ ...item }));
  category.value = purpose.value === 'default' ? 'material' : 'furniture';
}, { immediate: true });
const slotOf = (item) => item.surface || item.aperture_type || item.asset?.aperture_type || '';
const selectedFor = (type, slot) => objects.value
  .filter((item) => item.asset_category === type && slotOf(item) === slot).map((item) => item.asset_uuid);
const toggle = (asset, slot) => {
  const index = objects.value.findIndex((item) => item.asset_category === asset.category
    && slotOf(item) === slot && item.asset_uuid === asset.uuid);
  if (index !== -1) { objects.value.splice(index, 1); return; }
  if (asset.category !== 'furniture') {
    objects.value = objects.value.filter((item) => item.asset_category !== asset.category || slotOf(item) !== slot);
  }
  objects.value.push({
    asset_category: asset.category,
    asset_uuid: asset.uuid,
    asset,
    ...(['material', 'molding'].includes(asset.category) ? { surface: slot } : {}),
    ...(asset.category === 'aperture' ? { aperture_type: slot } : {}),
  });
};
const confirmClose = () => new Promise((resolve) => {
  Modal.confirm({
    title: 'Закрыть выбор объектов?',
    width: 560,
    wrapClassName: 'defaults-discard-confirm',
    okButtonProps: { danger: true, type: 'default' },
    cancelButtonProps: { type: 'primary' },
    content: 'Изменения в этом окне будут сброшены.',
    okText: 'Сбросить и закрыть',
    cancelText: 'Продолжить выбор',
    onOk: () => resolve(true),
    onCancel: () => resolve(false),
  });
});
const cancel = async () => { if (await confirmClose()) emit('close'); };
const save = () => Modal.confirm({
  title: 'Сохранить набор объектов?',
  content: `Типов комнат: ${rooms.value.length}. Объектов: ${objects.value.length}.`,
  okText: 'Сохранить',
  cancelText: 'Продолжить выбор',
  onOk: () => emit('save', {
    rooms: [...rooms.value], objects: objects.value.map((item) => ({ ...item, purpose: purpose.value })),
  }),
});
const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ''; };
onMounted(() => window.addEventListener('beforeunload', beforeUnload));
onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload));
defineExpose({ confirmClose });
</script>

<style scoped>
.room-field { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.room-actions { margin-left: auto; }
.purpose-field { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; margin-bottom: 20px; }
.purpose-hint { color: #8c8c8c; font-size: 13px; }
.modal-footer {
  display: flex; justify-content: flex-end; align-items: center; gap: 10px;
  border-top: 1px solid #eee; padding: 16px 0; margin-top: 16px;
  position: sticky; bottom: 0; background: white; z-index: 2;
}
.modal-footer span { margin-right: auto; color: #8c8c8c; }
</style>
